// Selectors.
import Selectors 			from '../../selectors.js';

// Config.
import Config				from '../../config/config.js';
import Enums				from '../../config/enums.js';

// Misc dependencices.
import GoalUtil 			from '../../utils/goal.js';
import MilestoneUtil		from '../../utils/milestone.js';

/**
 * State processing business logic.
 *
 * @namespace
 *
 * @memberof module:Goal.Model.Logic
 */
const StateProcessing = {

	/**
	 * Process Date Goal type state.
	 */
	dateGoal: function(id, metricId, strategies, dates, labels, progress) {

		// - - - - - - - - - - - - - - - - - -
		// Calculate participation booleans.
		let calculateCanUserParticipate = () => {

			let _canParticipate = false;

			// If the goal is in progress.
			if(GoalUtil.isGoalActive(dates)) {

				_canParticipate = true;
			}

			return _canParticipate;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate if the user has already participated.
		let calculateHasUserParticipated = () => {

			let _alreadyParticipated = false;

			_alreadyParticipated = GoalUtil.hasUserAlreadyParticipatedForGoal(metricId);

			return _alreadyParticipated;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate Goal message.
		let calculateGoalMessage = () => {

			let _goalMessage;

			// If goal has NOT started yet.
			if(!GoalUtil.hasGoalStarted(dates.start)) {

				_goalMessage = labels.goal.notStarted;

			// If the goal is in progress.
			} else if(GoalUtil.isGoalActive(dates)) {

				_goalMessage = labels.goal.open;

			// If the goal has ended.
			} else {

				_goalMessage = labels.goal.reached;
			}

			return _goalMessage;
		}

		// - - - - - - - - - - - - - - - - - -
		// Define overall state object.
		let state = {

			canParticipate: calculateCanUserParticipate(),
			hasParticipated:calculateHasUserParticipated(),

			milestones: 	null,

			messages: {
				goal: 		calculateGoalMessage(),
				milestone: 	null
			}
		};

		return state;
	},

	/**
	 * Process Number Goal type state.
	 */
	numberGoal: function(id, metricId, strategies, dates, labels, milestones, progress) {

		// - - - - - - - - - - - - - - - - - -
		// Calculate participation booleans.
		let calculateCanUserParticipate = () => {

			let _canParticipate = false;

			// If the goal is in progress.
			if(GoalUtil.isGoalActive(dates)) {

				// If overflow is allowed for this  goal.
				if(strategies.overflow === Enums.GOAL_EXCEED_STRATEGY_ALLOW) {

					_canParticipate = true;

				// If overflow is NOT allowed for this goal.
				} else {

					let _goalAlreadyReached = GoalUtil.hasGoalBeenReached(progress);

					// If the goal has not already been reached.
					if(!_goalAlreadyReached) {

						_canParticipate = true;
					}
				}
			}

			return _canParticipate;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate if the user has already participated.
		let calculateHasUserParticipated = () => {

			let _alreadyParticipated = false;

			_alreadyParticipated = GoalUtil.hasUserAlreadyParticipatedForGoal(metricId);

			return _alreadyParticipated;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate milestone states.
		let calculateMilestoneStates = () => {

			let _milestoneStates;

			// If we have some Milestones.
			if(milestones) {

				_milestoneStates = {};

				// Loop over Milestones.
				for(let milestoneKey in milestones) {

					// Grab the Milestone definition.
					let milestoneDef = milestones[milestoneKey];

					// Visual Milestones do not have an active period.
					let _period = Enums.MILESTONE_PERIOD_NONE;

					// Check if Milestone is reached/unreached.
					let _reached = MilestoneUtil.hasMilestoneBeenReached(milestoneDef, progress);

					// Visual Milestones do not gate user interaction.
					let _hasParticipated = false;

					// Store state for this milestone.
					_milestoneStates[milestoneDef.id] = {
						period: 		_period,
						reached: 		_reached,
						hasParticipated:_hasParticipated
					}
				}
			}

			return _milestoneStates;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate Milestone message.
		let calculateMilestoneMessage = () => {

			let _milestoneMessage;

			if(milestones && (strategies.milestone === Enums.GOAL_MILESTONE_STRATEGY_MILESTONE)) {

				// If the goal is in progress.
				if(GoalUtil.isGoalActive(dates)) {

					_milestoneMessage = labels.milestone.notReached;

					// If the goal HAS been reached early.
					if(GoalUtil.hasGoalBeenReached(progress)) {

						_milestoneMessage = null;
					}

				// If the goal has ended.
				} else {

					_milestoneMessage = '';
				}
			}

			return _milestoneMessage;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate Goal message.
		let calculateGoalMessage = () => {

			let _goalMessage;

			// If goal has NOT started yet.
			if(!GoalUtil.hasGoalStarted(dates.start)) {

				_goalMessage = labels.goal.notStarted;

			// If the goal is in progress.
			} else if(GoalUtil.isGoalActive(dates)) {

				_goalMessage = labels.goal.open;

				// If the goal HAS been reached early.
				if(GoalUtil.hasGoalBeenReached(progress)) {

					_goalMessage = labels.goal.reached;
				}

			// If the goal has ended.
			} else {

				// If the goal HAS been reached.
				if(GoalUtil.hasGoalBeenReached(progress)) {

					_goalMessage = labels.goal.reached;

				// If the goal has NOT been reached.
				} else {

					_goalMessage = labels.goal.notReached;
				}
			}

			return _goalMessage;
		}

		// - - - - - - - - - - - - - - - - - -
		// Define overall state object.
		let state = {

			canParticipate: calculateCanUserParticipate(),
			hasParticipated:calculateHasUserParticipated(),

			milestones: 	calculateMilestoneStates(),

			messages: {
				goal: 		calculateGoalMessage(),
				milestone: 	calculateMilestoneMessage()
			}
		};

		return state;
	},

	/**
	 * Process DateNumber Goal type state.
	 */
	dateNumberGoal: function(id, metricId, strategies, dates, labels, milestones, progress) {

		// - - - - - - - - - - - - - - - - - -
		// Calculate participation booleans.
		let calculateCanUserParticipate = () => {

			let _canParticipate = false;

			// If the goal is in progress.
			if(GoalUtil.isGoalActive(dates)) {

				// Switch logic based on milestone strategy.
				switch(strategies.milestone) {

					// No milestones/Simple milestones.
					case Enums.GOAL_MILESTONE_STRATEGY_TARGET:
					case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:

						// If overflow is allowed for this  goal.
						if(strategies.overflow === Enums.GOAL_EXCEED_STRATEGY_ALLOW) {

							_canParticipate = true;

						// If overflow is NOT allowed for this goal.
						} else {

							let _goalAlreadyReached = GoalUtil.hasGoalBeenReached(progress);

							// If the goal has not already been reached.
							if(!_goalAlreadyReached) {

								_canParticipate = true;
							}
						}

					break;

					// Gated milestones.
					case Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE:

						// If any milestone is currently active.
						if(MilestoneUtil.isAnyMilestoneActive(milestones)) {

							// If overflow is allowed for this  goal.
							if(strategies.overflow === Enums.GOAL_EXCEED_STRATEGY_ALLOW) {

								_canParticipate = true;

							// If overflow is NOT allowed for this goal.
							} else {

								// Get the active milestone.
								let currentMilestone = MilestoneUtil.getActiveMilestone(milestones);

								let _milestoneAlreadyReached = MilestoneUtil.hasMilestoneBeenReached(currentMilestone, progress);

								// If the milestone has not already been reached.
								if(!_milestoneAlreadyReached) {

									_canParticipate = true;
								}
							}
						}

					break;
				}
			}

			return _canParticipate;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate if the user has already participated.
		let calculateHasUserParticipated = () => {

			let _alreadyParticipated = false;

			// Switch logic based on milestone strategy.
			switch(strategies.milestone) {

				// No milestones/Simple milestones.
				case Enums.GOAL_MILESTONE_STRATEGY_TARGET:
				case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:

					_alreadyParticipated = GoalUtil.hasUserAlreadyParticipatedForGoal(metricId);

				break;

				// Gated milestones.
				case Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE:

					let _activeMilestone = MilestoneUtil.getMostRecentlyActiveMilestone(milestones);
					_alreadyParticipated = MilestoneUtil.hasUserAlreadyParticipatedForMilestone(metricId, _activeMilestone);

				break;
			}

			return _alreadyParticipated;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate milestone states.
		let calculateMilestoneStates = () => {

			let _milestoneStates = {};

			// If we have some Milestones.
			if(milestones) {

				// Loop over Milestones.
				for(let milestoneKey in milestones) {

					// Grab the Milestone definition.
					let milestoneDef = milestones[milestoneKey];

					// Detect if this segment is before/during/after its active period based on the start and end dates supplied.
					let _period;
					if(milestoneDef.startDate && milestoneDef.endDate) {

						// Before active period.
						if(!MilestoneUtil.hasMilestoneStarted(milestoneDef)) {

							_period = Enums.MILESTONE_PERIOD_BEFORE;

						// During active period.
						} else if (MilestoneUtil.isMilestoneActive(milestoneDef)) {

							_period = Enums.MILESTONE_PERIOD_DURING;

						// After active period.
						} else {

							_period = Enums.MILESTONE_PERIOD_AFTER;
						}
					}

					// Check if Milestone is reached/unreached.
					let _reached = MilestoneUtil.hasMilestoneBeenReached(milestoneDef, progress);

					// Check if user has already participated for this Milestone.
					let _hasParticipated = MilestoneUtil.hasUserAlreadyParticipatedForMilestone(metricId, milestoneDef)

					// Store state for this milestone.
					_milestoneStates[milestoneDef.id] = {
						period: 		_period,
						reached: 		_reached,
						hasParticipated:_hasParticipated
					}
				}
			}

			return _milestoneStates;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate Milestone message.
		let calculateMilestoneMessage = () => {

			let _milestoneMessage;

			if(milestones && (strategies.milestone === Enums.GOAL_MILESTONE_STRATEGY_MILESTONE || strategies.milestone === Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE)) {

				// If the goal is in progress.
				if(GoalUtil.isGoalActive(dates)) {

					// Switch logic based on milestone strategy.
					switch(strategies.milestone) {

						// Simple milestones.
						case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:

							_milestoneMessage = labels.milestone.notReached;

							// If the goal HAS been reached early.
							if(GoalUtil.hasGoalBeenReached(progress)) {

								_milestoneMessage = null;
							}

						break;

						// Gated milestones.
						case Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE:

							// Get the active milestone.
							let currentMilestone = MilestoneUtil.getMostRecentlyActiveMilestone(milestones);

							// If the active milestone has NOT yet been reached.
							if(!MilestoneUtil.hasMilestoneBeenReached(currentMilestone, progress)) {

								_milestoneMessage = labels.milestone.notReached;

							// If the current milestone HAS been reached.
							} else {

								_milestoneMessage = labels.milestone.reached;
							}

						break;
					}

					// If the goal HAS been reached early.
					if(GoalUtil.hasGoalBeenReached(progress)) {

						_milestoneMessage = null;
					}

				// If the goal has ended.
				} else {

					_milestoneMessage = '';
				}
			}

			return _milestoneMessage;
		}

		// - - - - - - - - - - - - - - - - - -
		// Calculate Goal message.
		let calculateGoalMessage = () => {

			let _goalMessage;

			// If goal has NOT started yet.
			if(!GoalUtil.hasGoalStarted(dates.start)) {

				_goalMessage = labels.goal.notStarted;

			// If the goal is in progress.
			} else if(GoalUtil.isGoalActive(dates)) {

				_goalMessage = labels.goal.open;

				// If the goal HAS been reached early.
				if(GoalUtil.hasGoalBeenReached(progress)) {

					_goalMessage = labels.goal.reached;
				}

			// If the goal has ended.
			} else {

				// If the goal HAS been reached.
				if(GoalUtil.hasGoalBeenReached(progress)) {

					_goalMessage = labels.goal.reached;

				// If the goal has NOT been reached.
				} else {

					_goalMessage = labels.goal.notReached;
				}
			}

			return _goalMessage;
		}

		// - - - - - - - - - - - - - - - - - -
		// Define overall state object.
		let state = {

			canParticipate: calculateCanUserParticipate(),
			hasParticipated:calculateHasUserParticipated(),

			milestones: 	calculateMilestoneStates(),

			messages: {
				goal: 		calculateGoalMessage(),
				milestone: 	calculateMilestoneMessage()
			}
		};

		return state;
	}
}

export default StateProcessing;